import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, BookOpen, Layers } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Notification from './Notification'; // Import Notification Component

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const courseData: { [key: string]: string[] } = {
  "Web Development": ["Java Full Stack", "MERN Full Stack", "Python Full Stack", "ReactJS Development", "Backend Development", "Frontend Development"],
  "App Development": ["Android Development", "iOS Development", "Flutter Development"],
  "Data Science & AI": ["Data Science with Python", "Machine Learning", "Artificial Intelligence", "Power BI & Visualization"],
  "Cloud & DevOps": ["AWS Cloud", "Azure", "DevOps Engineer", "Linux Administration"],
  "Programming": ["Core Java", "C++ Programming", "Python Programming", "Spring Boot", "Data Structures (DSA)"],
  "Database": ["SQL", "MySQL", "MongoDB"],
  "Software Testing": ["Manual Testing", "Automation (Selenium)", "API Testing"]
};

const RegistrationModal: React.FC<RegistrationModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- Notification State ---
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false,
    message: '',
    type: 'success'
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    category: "",
    course: ""
  });
  
  const [subCourses, setSubCourses] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && user) {
        setFormData(prev => ({
            ...prev,
            name: user.name || "",
            email: user.email || "",
            mobile: "" 
        }));
    } else if (isOpen && !user) {
        setFormData({ name: "", email: "", mobile: "", category: "", course: "" });
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    if (id === 'category') {
        setFormData({ ...formData, category: value, course: "" });
        setSubCourses(courseData[value] || []);
    } else if (id === 'subCourse') {
        setFormData({ ...formData, course: value });
    } else {
        setFormData({ ...formData, [id]: value });
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    onClose(); 
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
        // const response = await fetch('http://localhost:2025/api/registration/apply', {
        const response = await fetch('https://www.projectinfotech.in/api/registration/apply', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });

        if (response.ok) {
            // 1. Show Success Popup
            setNotify({
                isOpen: true,
                message: `Registration Successful! Confirmation sent to ${formData.email}.`,
                type: 'success'
            });

            // 2. Clear Form
            setFormData({ name: "", email: "", mobile: "", category: "", course: "" });

            // 3. Close Modal after delay (so user can see the success message)
            setTimeout(() => {
                handleClose();
            }, 2000); 

        } else {
            // Show Error Popup
            setNotify({
                isOpen: true,
                message: 'Something went wrong. Please try again.',
                type: 'error'
            });
        }
    } catch (error) {
        console.error("Registration Error:", error);
        setNotify({
            isOpen: true,
            message: 'Failed to connect to the server.',
            type: 'error'
        });
    } finally {
        setLoading(false);
    }
  };

  return (
    <>
      {/* --- Notification Component Added Here --- */}
      <Notification 
        isOpen={notify.isOpen} 
        message={notify.message} 
        type={notify.type} 
        onClose={() => setNotify({ ...notify, isOpen: false })} 
      />

      {/* Main Modal Code (Same as before, just rendering logic wrapped) */}
      {(isVisible || isAnimating) && (
        <div 
          className={`fixed inset-0 z-[100] flex items-center justify-center 
                      transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}
                      ${isVisible ? 'pointer-events-auto' : 'pointer-events-none'} 
                      backdrop-blur-sm`} 
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} 
          onClick={handleClose}
        >
          <div 
            className={`bg-white rounded-xl shadow-2xl w-full max-w-sm m-4 
                        transform transition-all duration-300 relative ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`} 
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 z-20 text-gray-500 hover:text-red-600 transition-colors duration-200 p-1 rounded-full bg-white shadow-lg"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div 
              className="p-6 pb-4 bg-gradient-to-br from-red-700 to-red-800 rounded-t-xl text-white relative overflow-hidden"
              style={{ 
                backgroundImage: `url('https://png.pngtree.com/thumb_back/fh260/background/20231109/pngtree-gorgeous-fire-flame-with-yellow-orange-and-red-hues-on-a-image_13806279.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: 'rgba(220, 38, 38, 0.85)', 
                mixBlendMode: 'multiply'
              }}
            >
                <div className="absolute inset-0 bg-red-800 opacity-70 z-0"></div> 
                <h2 className="text-xl font-extrabold text-center relative z-10 drop-shadow-md pt-4">Master IT Skills</h2>
                <h3 className="text-base font-semibold text-center mt-1 relative z-10 text-yellow-300 drop-shadow-sm">for a Brighter Future!</h3>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full Name"
                  required
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 bg-white shadow-sm ${user?.name ? 'bg-gray-50' : ''}`}
                />
              </div>

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  required
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 bg-white shadow-sm ${user?.email ? 'bg-gray-50' : ''}`}
                />
              </div>

              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  id="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 bg-white shadow-sm"
                />
              </div>
              
              <div className="relative">
                <BookOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  id="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-red-500 bg-white shadow-sm text-gray-700"
                >
                  <option value="">Select Domain / Category</option>
                  {Object.keys(courseData).map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {formData.category && (
                <div className="relative animate-fade-in-down">
                    <Layers className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                    id="subCourse"
                    value={formData.course}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-red-500 bg-white shadow-sm text-gray-700"
                    >
                    <option value="">Select Specific Course</option>
                    {subCourses.map((course) => (
                        <option key={course} value={course}>{course}</option>
                    ))}
                    </select>
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-md mt-6 disabled:bg-red-400" 
              >
                {loading ? (
                    <span className="flex items-center">
                        Processing...
                    </span>
                ) : "Submit Application"}
              </button>
            </form>
            
            <p className="text-center text-xs text-gray-500 p-4 pt-0 border-t border-gray-100">
                Secure your spot today. <span className="text-red-600">Limited seats available.</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default RegistrationModal;